/**
 * History store.
 *
 * Tracks recent AI interactions (chat, formula, analysis, cleaning, custom functions).
 * Persisted to localStorage with a capped size.
 *
 * Strategy:
 *   - Module-level cache for fast reads.
 *   - localStorage for persistence across reloads.
 *   - Entries are prepended (newest first).
 *   - Oldest entries are evicted when the cap is reached.
 *   - No React context/provider: consumers call getHistory() on-demand.
 */

import type { HistoryEntry as BaseHistoryEntry, HistoryActionType } from '@excel-ai-studio/shared';

const STORAGE_KEY = 'excel-ai-studio-history';
const MAX_ENTRIES = 100;

let cached: HistoryEntry[] | null = null;

export interface HistoryEntry extends BaseHistoryEntry {
  readonly module?: string;
  readonly durationMs?: number;
  readonly sheetName?: string;
  readonly rangeAddress?: string;
}

// ─── Public API ──────────────────────────────────────────

/** Get all history entries (newest first). Always returns a valid array. */
export function getHistory(): readonly HistoryEntry[] {
  if (cached) return cached;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        cached = parsed.filter(isValidEntry).slice(0, MAX_ENTRIES);
        return cached;
      }
    }
  } catch {
    // Corrupted data — reset silently
  }

  cached = [];
  return cached;
}

/** Add a new history entry. Returns the updated list. */
export function addHistoryEntry(
  type: HistoryActionType,
  provider: string,
  model: string,
  request: string,
  success: boolean,
  error?: string,
  meta?: {
    module?: string;
    durationMs?: number;
    sheetName?: string;
    rangeAddress?: string;
  },
): readonly HistoryEntry[] {
  const entry: HistoryEntry = {
    id: generateId(),
    type,
    timestamp: Date.now(),
    provider,
    model,
    request: request.slice(0, 500), // cap for storage
    success,
    error: error?.slice(0, 200),
    module: meta?.module,
    durationMs: meta?.durationMs,
    sheetName: meta?.sheetName,
    rangeAddress: meta?.rangeAddress,
  };

  const current = [...getHistory()];
  current.unshift(entry);

  // Evict oldest
  if (current.length > MAX_ENTRIES) {
    current.length = MAX_ENTRIES;
  }

  cached = current;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
  } catch {
    // Storage full — silently ignore
  }

  return cached;
}

/** Clear all history. */
export function clearHistory(): readonly HistoryEntry[] {
  cached = [];
  localStorage.removeItem(STORAGE_KEY);
  return cached;
}

/** Export full history to JSON (pretty-printed). */
export function exportHistoryJson(): string {
  return JSON.stringify(getHistory(), null, 2);
}

/** Get entries filtered by type. */
export function getHistoryByType(type: HistoryActionType): readonly HistoryEntry[] {
  return getHistory().filter((e) => e.type === type);
}

// ─── Validation ──────────────────────────────────────────

function isValidEntry(item: unknown): item is HistoryEntry {
  if (typeof item !== 'object' || item === null) return false;
  const e = item as Record<string, unknown>;
  return (
    typeof e.id === 'string' &&
    typeof e.type === 'string' &&
    typeof e.timestamp === 'number' &&
    typeof e.provider === 'string' &&
    typeof e.model === 'string' &&
    typeof e.request === 'string' &&
    typeof e.success === 'boolean'
  );
}

// ─── Helpers ─────────────────────────────────────────────

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
